import { useParams } from "react-router-dom";
import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { Tab } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import ArticleIcon from "../Icons/ArticleIcon";
import CertificateIcon from "../Icons/CertificateIcon";
import VideoIcon from "../Icons/VideoIcon";
import CoursePreview from "./CoursePreview";
import Button from "../Shared/Button";
import ArrowIcon from "../Icons/ArrowIcon";
import { GetOfflineCourseById } from "../../api/OfflineCourses/GetOfflineCourseById";
import CoursePreviewIcon from "../Icons/CoursePreviewIcon";
import CourseContentIcon from "../Icons/CourseContentIcon";
import CourseContent from "./CourseContent";
import PlayIcon from "../Icons/PlayIcon";
import Spinner from "../Shared/Spinner";
import { getUserCountry } from "../../utils/utils";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet";
const OfflineCourseMain = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { id } = useParams();
    const courseId = id || "";
    const [country, setCountry] = useState<string | null>(null);

    useEffect(() => {
        const userCountry = getUserCountry();
        setCountry(userCountry);
    }, []);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };
    const {
        data: CourseByIdDTO,
        isLoading: isCourseLoading,
        // Course videos travel with expiring VdoCipher pairs; the players need
        // the payload's age and a way to re-mint. See VdoCipherPlayer.
        dataUpdatedAt: courseFetchedAt,
        refetch: refetchCourse,
    } = useQuery({
        queryFn: () => GetOfflineCourseById(courseId),
        queryKey: ["courses", courseId],
        staleTime: 0,
        refetchInterval: 0,
    });
    const refreshCipher = useCallback(() => {
        refetchCourse();
    }, [refetchCourse]);
    const courses = CourseByIdDTO?.data;


    if (isCourseLoading)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spinner />
            </div>
        );

    const discountPercentage = courses?.discountPercentage || 0;
    const discountedPrice =
        country === "EG"
            ? courses?.actualPrice_egp
                ? courses.actualPrice_egp -
                (courses.actualPrice_egp * discountPercentage) / 100
                : 0
            : courses?.actualPrice_usd
                ? courses.actualPrice_usd -
                (courses.actualPrice_usd * discountPercentage) / 100
                : 0;

    return (
        <div className="container xl:pt-20 lg:pt-28 md:pt-32 sm:pt-20 pt-28">
            <Helmet>
                <link
                    rel="canonical"
                    href={`https://www.nilewisect.com/course/${courses?.name}`}
                />
                <meta name="robots" content="index, follow" />
            </Helmet>

            <div className="sm:pt-12 ">
                <div className="w-full relative md:h-[600px] sm:h-[450px] h-[250px] rounded-2xl overflow-hidden">
                    <video
                        ref={videoRef}
                        src={courses?.preview_video}
                        className="w-full h-full object-cover"
                        poster={courses?.img_url}
                        onClick={togglePlay}
                    ></video>
                    {!isPlaying && (
                        <>
                            <div className="w-full h-full absolute bg-black/70 z-50 inset-0"></div>
                            <div className="w-[100px] h-[100px] bg-primary/70 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 rounded-full justify-center items-center flex">
                                <button
                                    className="bg-primary h-20 w-20 rounded-full flex items-center justify-center "
                                    onClick={togglePlay}
                                >
                                    <PlayIcon />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="flex justify-between gap-10 sm:pt-8 pt-5 pb-32 xl:flex-row flex-col">
                <div className="w-full">
                    <Tab.Group>
                        <Tab.List className="bg-primary bg-opacity-[0.07] gap-1.5 flex sm:w-fit w-full sm:p-1.5 p-1 border border-primary/5 rounded-full">
                            <Tab as={Fragment}>
                                {({ selected }) => (
                                    <button
                                        className={` flex gap-1 justify-center h-8 sm:w-48 w-full rounded-full items-center outline-none transition-all duration-300 ease-out sm:text-base text-sm
                      ${selected
                                                ? "text-primary bg-white border border-[#CFDBEC] "
                                                : " text-primary/70"
                                            }
                    `}
                                    >
                                        <CoursePreviewIcon /> Course Preview
                                    </button>
                                )}
                            </Tab>
                            <Tab as={Fragment}>
                                {({ selected }) => (
                                    <button
                                        className={` flex gap-1 justify-center h-8 sm:w-48 w-full rounded-full items-center outline-none transition-all duration-300 ease-out sm:text-base text-sm
                      ${selected
                                                ? "text-primary bg-white border border-[#CFDBEC] "
                                                : " text-primary/70"
                                            }
                    `}
                                    >
                                        <CourseContentIcon /> Course Content
                                    </button>
                                )}
                            </Tab>
                        </Tab.List>
                        <Tab.Panels>
                            <Tab.Panel>
                                <CoursePreview
                                    courses={courses}
                                    cipherFetchedAt={courseFetchedAt}
                                    onRefreshCipher={refreshCipher}
                                />
                            </Tab.Panel>
                            <Tab.Panel>
                                <CourseContent
                                    courses={courses}
                                    cipherFetchedAt={courseFetchedAt}
                                    onRefreshCipher={refreshCipher}
                                />
                            </Tab.Panel>
                        </Tab.Panels>
                    </Tab.Group>
                </div>
                <div className="xl:space-y-7 xl:block flex lg:flex-row flex-col  lg:gap-10 gap-6">
                    <div className="sm:py-9 xl:w-[429px] w-full sm:px-6 p-[18px] border border-black/15 shadow rounded-2xl">
                        <span className="text-lightGray font-medium sm:text-0.5xl  mb-2.5 sm:mb-3">
                            Full Course
                        </span>
                        <div className="flex justify-between items-center mb-2 sm:mb-7">
                            <h1 className="text-primary font-bold text-lg sm:text-[30px] -tracking-[0.02em] flex items-center gap-4">
                                {discountedPrice} {country === "EG" ? "EGP" : "USD"}{" "}
                                {discountPercentage > 0 && (
                                    <span className="text-lightGray font-medium sm:text-0.5xl line-through">
                                        {country === "EG" ? (
                                            <>{courses?.actualPrice_egp} EGP</>
                                        ) : (
                                            <>{courses?.actualPrice_usd} USD</>
                                        )}
                                    </span>
                                )}
                            </h1>
                            {discountPercentage > 0 && (
                                <span className="mr-2 bg-[#FF0000]/5 py-2.5 leading-none px-2.5 rounded-lg text-[#FF0000]/70 font-semibold text-lg">
                                    {discountPercentage}%
                                </span>
                            )}
                        </div>
                        <h1 className="text-burntBlack font-bold sm:text-0.5xl tracking-[0.02em] mb-3 sm:mb-5">
                            Course Includes:
                        </h1>
                        <div className="font-medium sm:text-xl text-sm text-lightGray sm:space-y-2.5 space-y-2 mb-4 sm:mb-8">
                            <div className="flex gap-2.5 items-center">
                                <VideoIcon className="sm:size-auto size-5" />{" "}
                                {courses?.num_hours} Hours On-Demand Video
                            </div>
                            <div className="flex gap-2.5 items-center">
                                <ArticleIcon className="sm:size-auto size-5" />{" "}
                                {courses?.chapters.length} Modules
                            </div>
                            <div className="flex gap-2.5 items-center">
                                <CertificateIcon className="sm:size-auto size-5" /> Certificate
                                of Completion{" "}
                            </div>
                        </div>
                        <Link
                            target="_blank"
                            to={courses?.googleForm || ''}
                        >
                            <Button
                                variant="primary"
                                className="w-full sm:!h-16 !h-11 sm:!text-xl !text-base mb-3"
                            >
                                Apply Now
                            </Button>
                        </Link>
                        <p className="tracking-[0.02em] sm:text-base text-sm font-medium text-center">
                            Trusted Learning Experience
                        </p>
                    </div>
                    <div className="sm:pt-10 xl:w-[429px] w-full  sm:pb-12 sm:px-8  p-[18px] border border-black/15 shadow rounded-2xl">
                        <div className="flex justify-between sm:mb-7 mb-5">
                            <h1 className="text-burntBlack font-bold sm:text-0.5xl tracking-[0.02em]">
                                Publisher
                            </h1>
                            <Link
                                to={`/instructor/${courses?.Instructor.id}`}
                                className="flex gap-0.5 items-center text-lightGray sm:text-lg text-sm font-medium"
                            >
                                View Profile{" "}
                                <ArrowIcon className="sm:size-5 size-3 rotate-180" />{" "}
                            </Link>
                        </div>
                        <div className="flex items-center sm:gap-4 gap-2.5 mb-4">
                            <div className="sm:size-[60px] size-12 rounded-full overflow-hidden border border-black/20">
                                <img
                                    className="object-cover object-[25%_75%] "
                                    src={courses?.Instructor.photo}
                                    alt="Instructor"
                                />
                            </div>
                            <div className="text-burntBlack font-bold sm:text-xl">
                                <h1 className="mb-0.5 sm:text-lg -tracking-[0.02em]">
                                    Dr. {courses?.Instructor.firstName}{" "}
                                    {courses?.Instructor.lastName}
                                </h1>
                                <p className="text-lightGray font-medium ">Instructor</p>
                            </div>
                        </div>
                        <p className="font-medium text-lightGray sm:text-base text-sm mb-5">
                            <ReactMarkdown>{courses?.Instructor.description}</ReactMarkdown>
                        </p>
                        <div className="flex 2xl:justify-between gap-10">
                            <div className="flex gap-2.5 items-center font-medium text-sm sm:text-xl text-lightGray">
                                <VideoIcon className="sm:size-auto size-5" />{" "}
                                {courses?.Instructor.count_students} Students
                            </div>
                            <div className="flex gap-2.5 items-center font-medium text-sm sm:text-xl text-lightGray">
                                <ArticleIcon className="sm:size-auto size-5" />{" "}
                                {courses?.Instructor.count_courses} Courses
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OfflineCourseMain;
